import {
  AddressDetails,
  DirectDebitDetails,
  DocumentTreeItem,
  JourneyState,
  PersonalDetailType,
  ProductType,
  QuoteProduct,
  VehicleState,
  VinWarrantyExpiry
} from '../components/common/CommonTypes';
import { RegistrationState } from '../consts/Common';

export type Action =
  | { type: 'set-vehicle'; vehicle: VehicleState }
  | { type: 'set-current-mileage'; currentMileage: number | null }
  | { type: 'set-vehicle-purchase-price'; vehiclePurchasePrice: number }
  | { type: 'set-vehicle-delivery-date'; vehicleDeliveryDate: string }
  | {
      type: 'set-vehicle-gap-introduction-date';
      vehicleGapIntroductionDate: string;
    }
  | { type: 'set-sales-id'; salesId: string }
  | { type: 'set-change-vehicle-vin'; vehicleIdentificationNumber: string }
  | { type: 'set-products'; products: ProductType[] }
  | { type: 'set-quote-products'; quoteProducts: QuoteProduct[] }
  | { type: 'set-documents'; documents: DocumentTreeItem[] }
  | {
      type: 'set-personal-details';
      personalDetail: PersonalDetailType;
    }
  | {
      type: 'set-direct-debit-details';
      directDebitDetails: DirectDebitDetails;
    }
  | { type: 'set-address-single-payment'; addressDetails: AddressDetails }
  | { type: 'set-agreed-terms-and-conditions'; hasAgreed: boolean }
  | {
      type: 'set-order-numbers';
      orderNumbers: string[];
      authorisationCode: string;
    }
  | { type: 'set-registration-fail-msg'; message: string }
  | { type: 'set-registration-state'; regState: number }
  | {
      type: 'set-nav-status';
      key: string;
      value: boolean;
    }
  | { type: 'set-expanded-node-ids'; expandedDocIds: string[] }
  | { type: 'set-included-quotes-data'; foundExistingQuotes: boolean }
  | {
      type: 'set-is-late-renewal';
      isLateRenewal: boolean;
    }
  | {
      type: 'set-policy-start-date';
      policyStartDate: Date;
    }
  | {
      type: 'set-quote-id';
      quoteId: number;
    }
  | {
      type: 'set-quote-expired';
      hasQuoteExpired: boolean;
    }
  | {
      type: 'set-vehicle-vrn';
      vrn: string;
    }
  | {
      type: 'set-message-acknowledged';
      isExpiredMessageAcknowledged: boolean;
    }
  | {
      type: 'set-exceeds-upsell-mileage-limit';
      exceedsUpsellMileageLimit: boolean;
    }
  | {
      type: 'set-quoted-by-dealer';
      quotedByDealerReference: string;
    }
  | {
      type: 'set-consent-to-renewal-marketing';
      renewalMarketing: boolean;
    }
  | {
      type: 'set-journey-state';
      journeyState: JourneyState;
    }
  | {
      type: 'set-expiry-details';
      expiryDetails: VinWarrantyExpiry;
    };

const JourneyReducer = (state: JourneyState, action: Action): JourneyState => {
  switch (action.type) {
    case 'set-journey-state':
      return { ...action.journeyState };
    case 'set-vehicle':
      if (JSON.stringify(state.vehicle) === JSON.stringify(action.vehicle)) {
        return state;
      }

      return {
        ...state,
        vehicle: action.vehicle,
        products: [],
        documents: []
      };

    case 'set-current-mileage':
      if (action.currentMileage !== state.vehicle.currentMileage) {
        return {
          ...state,
          vehicle: {
            ...state.vehicle,
            currentMileage: action.currentMileage
          },
          products: []
        };
      }

      return state;

    case 'set-vehicle-purchase-price':
      if (action.vehiclePurchasePrice !== state.vehicle.vehiclePurchasePrice) {
        return {
          ...state,
          vehicle: {
            ...state.vehicle,
            vehiclePurchasePrice: action.vehiclePurchasePrice
          },
          products: []
        };
      }

      return state;

    case 'set-vehicle-delivery-date':
      return {
        ...state,
        vehicle: {
          ...state.vehicle,
          vehicleDeliveryDate: action.vehicleDeliveryDate
        }
      };

    case 'set-vehicle-vrn':
      return {
        ...state,
        vehicle: {
          ...state.vehicle,
          vehicleDetails: {
            ...state.vehicle.vehicleDetails,
            vrn: action.vrn
          }
        }
      };

    case 'set-vehicle-gap-introduction-date':
      return {
        ...state,
        vehicle: {
          ...state.vehicle,
          vehicleGapIntroductionDate: action.vehicleGapIntroductionDate
        }
      };

    case 'set-products':
      return {
        ...state,
        products: action.products
      };

    case 'set-quote-products':
      return {
        ...state,
        quoteProducts: action.quoteProducts
      };

    case 'set-documents':
      return {
        ...state,
        documents: action.documents
      };

    case 'set-personal-details':
      return {
        ...state,
        personalDetail: action.personalDetail
      };

    case 'set-direct-debit-details':
      return {
        ...state,
        directDebitDetails: action.directDebitDetails
      };

    case 'set-agreed-terms-and-conditions':
      return {
        ...state,
        agreedTermsAndConditions: action.hasAgreed
      };

    case 'set-address-single-payment':
      return {
        ...state,
        singlePaymentDetails: {
          ...state.singlePaymentDetails,
          billingAddress: action.addressDetails
        }
      };

    case 'set-order-numbers':
      return {
        ...state,
        orderNumbers: action.orderNumbers,
        registrationFailMsg: '',
        bankAuthorisationCode: action.authorisationCode,
        registrationState: RegistrationState.Success
      };

    case 'set-registration-fail-msg':
      return {
        ...state,
        registrationFailMsg: action.message,
        registrationState: RegistrationState.Failed
      };

    case 'set-registration-state':
      return {
        ...state,
        registrationState: action.regState
      };

    case 'set-expanded-node-ids':
      return {
        ...state,
        expandedDocIds: action.expandedDocIds
      };

    case 'set-nav-status':
      return {
        ...state,
        navigationStatus: {
          ...state.navigationStatus,
          [action.key]: action.value
        }
      };
    case 'set-included-quotes-data':
      return {
        ...state,
        foundExistingQuotes: action.foundExistingQuotes
      };

    case 'set-is-late-renewal':
      return {
        ...state,
        isLateRenewal: action.isLateRenewal
      };

    case 'set-policy-start-date':
      return {
        ...state,
        policyStartDate: action.policyStartDate
      };

    case 'set-quote-id':
      return {
        ...state,
        quoteId: action.quoteId
      };

    case 'set-quote-expired':
      return {
        ...state,
        hasQuoteExpired: action.hasQuoteExpired
      };

    case 'set-message-acknowledged':
      return {
        ...state,
        isExpiredMessageAcknowledged: action.isExpiredMessageAcknowledged
      };

    case 'set-exceeds-upsell-mileage-limit':
      return {
        ...state,
        exceedsUpsellMileageLimit: action.exceedsUpsellMileageLimit
      };
    case 'set-quoted-by-dealer':
      return {
        ...state,
        quotedByDealerReference: action.quotedByDealerReference
      };

    case 'set-consent-to-renewal-marketing':
      return {
        ...state,
        renewalMarketing: action.renewalMarketing
      };

    case 'set-expiry-details':
      return {
        ...state,
        expiryDetails: action.expiryDetails
      };

    default:
      return state;
  }
};

export default JourneyReducer;
